import React, { useState } from 'react';
import { ShoppingBag, DollarSign, Home, Users, Wallet, ChevronDown, Calendar, Repeat, ClipboardList } from 'lucide-react';
import { Transaction } from '../store/useFinanceStore';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';

interface TransactionItemProps {
  transaction: Transaction;
}

const TransactionItem: React.FC<TransactionItemProps> = ({ transaction }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Mercado':
        return ShoppingBag;
      case 'Moradia':
        return Home;
      case 'Entrada principal':
        return Wallet;
      case 'Freelance':
        return DollarSign;
      default:
        return Users;
    }
  };

  const Icon = getCategoryIcon(transaction.category);
  const isPositive = transaction.amount > 0;
  const isCancelled = transaction.status === 'cancelled';
  const recurrenceEndDate = transaction.recurrenceEndDate ? new Date(transaction.recurrenceEndDate) : null;
  const confirmationDate = transaction.confirmationDate ? new Date(transaction.confirmationDate) : null;
  const recurrenceLabel = transaction.recurrenceType && transaction.recurrenceType !== 'none'
    ? transaction.recurrenceType
    : null;

  const statusTone = (() => {
    const toneMap = {
      paid: isDark
        ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
        : 'bg-emerald-100 text-emerald-700 border border-emerald-200',
      confirmed: isDark
        ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
        : 'bg-emerald-100 text-emerald-700 border border-emerald-200',
      active: isDark
        ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
        : 'bg-emerald-100 text-emerald-700 border border-emerald-200',
      scheduled: isDark
        ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
        : 'bg-amber-100 text-amber-700 border border-amber-200',
      overdue: isDark
        ? 'bg-red-500/15 text-red-300 border border-red-500/30'
        : 'bg-rose-100 text-rose-600 border border-rose-200',
      cancelled: isDark
        ? 'bg-slate-700/50 text-slate-300 border border-slate-600/60'
        : 'bg-slate-100 text-slate-500 border border-slate-200',
      default: isDark
        ? 'bg-slate-700/40 text-slate-200 border border-slate-600/50'
        : 'bg-slate-100 text-slate-600 border border-slate-200',
    } as const;

    const key = (transaction.status ?? 'default') as keyof typeof toneMap;
    return toneMap[key] ?? toneMap.default;
  })();

  return (
    <div
      className={`rounded-xl border transition-all duration-200 ${
        isDark
          ? 'border-slate-700/60 bg-slate-800/60 backdrop-blur-sm'
          : 'border-teal-100 bg-white shadow-sm'
      } ${isCancelled ? 'opacity-70' : ''}`}
    >
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`w-full flex items-center gap-4 px-4 py-4 text-left transition-colors ${
          isDark ? 'hover:bg-slate-800/80' : 'hover:bg-teal-50'
        }`}
        aria-expanded={isOpen}
      >
        <div
          className={`p-2 rounded-lg ${
            isPositive
              ? isDark
                ? 'bg-emerald-500/15'
                : 'bg-emerald-100'
              : isDark
                ? 'bg-rose-500/15'
                : 'bg-rose-100'
          }`}
        >
          <Icon
            size={20}
            className={
              isPositive
                ? isDark
                  ? 'text-emerald-300'
                  : 'text-emerald-600'
                : isDark
                  ? 'text-rose-300'
                  : 'text-rose-600'
            }
          />
        </div>
        <div className="flex-1">
          <p
            className={`text-base font-semibold flex flex-wrap items-center gap-2 ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}
          >
            {isCancelled && (
              <span
                className={`text-xs uppercase tracking-wide ${
                  isDark ? 'text-rose-300' : 'text-rose-600'
                }`}
              >
                Cancelada
              </span>
            )}
            {transaction.description}
          </p>
          <div
            className={`flex flex-wrap items-center gap-3 text-xs mt-1 ${
              isDark ? 'text-slate-400' : 'text-slate-500'
            }`}
          >
            <span className="inline-flex items-center gap-1">
              <Calendar size={12} />
              {format(new Date(transaction.date), 'dd/MM/yyyy', { locale: ptBR })}
            </span>
            <span className={`inline-flex items-center gap-2 text-[11px] px-2 py-1 rounded-full ${statusTone}`}>
              {transaction.status?.toUpperCase() ?? 'SEM STATUS'}
            </span>
            {transaction.isForecast && (
              <span
                className={`text-[11px] uppercase ${
                  isDark ? 'text-amber-300' : 'text-amber-600'
                }`}
              >
                Previsto
              </span>
            )}
          </div>
        </div>
        <div className="text-right">
          <p
            className={`text-lg font-semibold ${
              isPositive
                ? isDark
                  ? 'text-emerald-300'
                  : 'text-emerald-600'
                : isDark
                  ? 'text-rose-300'
                  : 'text-rose-600'
            }`}
          >
            {isPositive ? '+' : '-'}R$ {Math.abs(transaction.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
          </p>
          <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            {transaction.category}
          </p>
        </div>
        <ChevronDown
          size={18}
          className={`transition-transform ${
            isDark ? 'text-slate-300' : 'text-slate-500'
          } ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className={`px-4 pb-4 text-sm ${isDark ? 'text-slate-200' : 'text-slate-600'}`}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <p
                  className={`flex items-center gap-2 ${
                    isDark ? 'text-slate-300' : 'text-slate-500'
                  }`}
                >
                  <ClipboardList
                    size={14}
                    className={isDark ? 'text-slate-400' : 'text-teal-500'}
                  />
                  <span className={`font-medium ${isDark ? 'text-slate-100' : 'text-slate-700'}`}>
                    Detalhes
                  </span>
                </p>
                <ul className={`space-y-1 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  <li>
                    <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>Tipo:</span>{' '}
                    {transaction.type === 'income' ? 'Entrada' : 'Saída'}
                  </li>
                  <li>
                    <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>Categoria:</span>{' '}
                    {transaction.customCategoryName || transaction.category}
                  </li>
                  {transaction.customSubcategoryName && (
                    <li>
                      <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>Subcategoria:</span>{' '}
                      {transaction.customSubcategoryName}
                    </li>
                  )}
                  {transaction.notes && (
                    <li>
                      <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>Observações:</span>{' '}
                      {transaction.notes}
                    </li>
                  )}
                  {isCancelled && transaction.cancellationReason && (
                    <li>
                      <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>
                        Motivo do cancelamento:
                      </span>{' '}
                      {transaction.cancellationReason}
                    </li>
                  )}
                </ul>
              </div>

              <div className="space-y-2">
                <p
                  className={`flex items-center gap-2 ${
                    isDark ? 'text-slate-300' : 'text-slate-500'
                  }`}
                >
                  <Repeat size={14} className={isDark ? 'text-slate-400' : 'text-teal-500'} />
                  <span className={`font-medium ${isDark ? 'text-slate-100' : 'text-slate-700'}`}>
                    Recorrência & pagamento
                  </span>
                </p>
                <ul className={`space-y-1 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  <li>
                    <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>Recorrência:</span>{' '}
                    {recurrenceLabel ? recurrenceLabel : 'Não recorrente'}
                  </li>
                  {transaction.recurrenceCount && (
                    <li>
                      <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>Repetições:</span>{' '}
                      {transaction.recurrenceCount}
                    </li>
                  )}
                  {recurrenceEndDate && !Number.isNaN(recurrenceEndDate.getTime()) && (
                    <li>
                      <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>Fim:</span>{' '}
                      {format(recurrenceEndDate, 'dd/MM/yyyy', { locale: ptBR })}
                    </li>
                  )}
                  {confirmationDate && !Number.isNaN(confirmationDate.getTime()) && (
                    <li>
                      <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>Confirmado em:</span>{' '}
                      {format(confirmationDate, 'dd/MM/yyyy', { locale: ptBR })}
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TransactionItem;